/**
 * Chef Reminders Service
 * 
 * Handles intelligent reminder timing and logic for live cooking sessions.
 * Provides photo reminders and check-in prompts at sensible moments.
 * Now timeline-aware and integrates with structured session reports.
 */

import liveSessionEvents from './liveSessionEvents';
import sessionReportService from './sessionReportService';

class ChefRemindersService {
    constructor() {
        this.isEnabled = false;
        this.reminderHistory = new Map(); // Track when reminders were last shown
        this.sessionContext = null;
        this.reminderCallbacks = {
            onPhotoReminder: null,
            onCheckInReminder: null
        };
        this.userToken = null; // Store user token for API calls
        
        // Reminder timing configuration
        this.config = {
            unified: {
                maxPerSession: 10,            // Maximum 10 reminders per session
                minIntervalMinutes: 5,        // Minimum 5 minutes between any reminders
                maxReminders: 10               // Distribute up to 10 reminders
            }
        };
        
        // Add session-wide counters
        this.sessionCounters = {
            reminders: 0,
            photos: 0,
            checkIns: 0
        };
        
        // Intelligent reminder scheduling
        this.reminderSchedule = [];
        this.sessionStartTime = null;
        
        this.setupEventListeners();
    }

    /**
     * Enable or disable Chef reminders
     */
    setEnabled(enabled) {
        this.isEnabled = enabled;
        if (enabled) {
            // console.log('🔔 Chef Reminders enabled');
            // Re-register event listeners in case they were cleaned up
            this.setupEventListeners();
        } else {
            // console.log('🔕 Chef Reminders disabled');
            this.clearReminderHistory();
        }
    }

    /**
     * Set session context for reminder timing
     */
    setSessionContext(context) {
        // console.log('🔔 Setting session context:', context);
        this.sessionContext = context;
    }

    /**
     * Set user token for API calls
     */
    setUserToken(token) {
        this.userToken = token;
    }

    /**
     * Register callback functions for reminder UI
     */
    setCallbacks(callbacks) {
        this.reminderCallbacks = { ...this.reminderCallbacks, ...callbacks };
    }

    /**
     * Setup event listeners for live session events
     */
    setupEventListeners() {
        // console.log('🔔 ChefReminders: Setting up event listeners');
        
        // First cleanup any existing listeners to avoid duplicates
        this.cleanup();
        
        // Store bound references so we can unsubscribe later
        this.boundHandlers = {
            sessionStart: this.handleSessionStart.bind(this),
            phaseChange: this.handlePhaseChange.bind(this),
            timerTick: this.handleTimerTick.bind(this),
            sessionEnd: this.handleSessionEnd.bind(this)
        };
        
        // Subscribe with stored references
        liveSessionEvents.subscribe('session.start', this.boundHandlers.sessionStart);
        liveSessionEvents.subscribe('phase.change', this.boundHandlers.phaseChange);
        liveSessionEvents.subscribe('timer.tick', this.boundHandlers.timerTick);
        liveSessionEvents.subscribe('session.end', this.boundHandlers.sessionEnd);
        
        // Verify subscriptions were registered
        // console.log('🔔 Event listener counts:', {
        //     'session.start': liveSessionEvents.getListenerCount('session.start'),
        //     'phase.change': liveSessionEvents.getListenerCount('phase.change'),
        //     'timer.tick': liveSessionEvents.getListenerCount('timer.tick'),
        //     'session.end': liveSessionEvents.getListenerCount('session.end')
        // });
    }

    /**
     * Handle session start event
     */
    handleSessionStart(event) {
        console.log('🔍 CHEF REMINDERS: handleSessionStart called', {
            isEnabled: this.isEnabled,
            hasContext: !!event.context,
            event: event
        });
        
        if (!this.isEnabled) {
            console.log('⚠️ Chef Reminders not enabled, skipping session start');
            return;
        }
        
        console.log('✅ Chef Reminders handling session start');
        this.setSessionContext(event.context);
        this.clearReminderHistory();
        this.sessionStartTime = Date.now();
        
        // Calculate intelligent reminder schedule
        this.calculateReminderSchedule(event.context);
    }

    /**
     * Handle phase change event (step transitions, prerequisites to active)
     */
    handlePhaseChange(event) {
        console.log('🔍 CHEF REMINDERS: handlePhaseChange called', {
            isEnabled: this.isEnabled,
            hasContext: !!event.context,
            phase: event.newPhase
        });
        
        if (!this.isEnabled) return;
        
        this.setSessionContext(event.context);
        
        // Check if it's time for a reminder based on intelligent schedule
        this.checkReminderSchedule();
    }

    /**
     * Handle timer tick event (every minute)
     */
    handleTimerTick(event) {
        if (!this.isEnabled) return;
        
        this.setSessionContext(event.context);
        
        // Note: Timer-based reminders are now handled by phase-based triggers
        // to reduce frequency and avoid overwhelming the user
    }

    /**
     * Handle session end event
     */
    handleSessionEnd(event) {
        if (!this.isEnabled) return;
        
        // Final check-in before session ends
        if (event.data.wasCompleted) {
            this.triggerUnifiedReminder('session_end', 'Congratulations! How did the cooking go overall?');
        }
    }

    /**
     * Calculate intelligent reminder schedule based on recipe duration
     */
    calculateReminderSchedule(context) {
        if (!context || !context.totalDurationMinutes) {
            console.warn('Cannot calculate reminder schedule: missing context or duration');
            return;
        }

        const totalDuration = context.totalDurationMinutes;
        const maxReminders = Math.min(this.config.unified.maxReminders, Math.floor(totalDuration / this.config.unified.minIntervalMinutes));
        
        if (maxReminders === 0) {
            console.log('Recipe too short for reminders');
            return;
        }

        // Distribute reminders evenly across the session duration
        const interval = totalDuration / maxReminders;
        this.reminderSchedule = [];
        
        for (let i = 1; i <= maxReminders; i++) {
            const reminderTime = interval * i;
            this.reminderSchedule.push({
                timeMinutes: reminderTime,
                triggered: false
            });
        }

        console.log(`✅ Reminder schedule: ${maxReminders} reminders for ${totalDuration}min session at:`, 
            this.reminderSchedule.map(r => `${r.timeMinutes.toFixed(1)}min`).join(', '));
    }

    /**
     * Check if it's time for a reminder based on the schedule
     */
    checkReminderSchedule() {
        if (!this.sessionStartTime || this.reminderSchedule.length === 0) {
            console.log('🔍 Skipping reminder check:', {
                hasSessionStartTime: !!this.sessionStartTime,
                scheduleLength: this.reminderSchedule.length
            });
            return;
        }

        const elapsedMinutes = (Date.now() - this.sessionStartTime) / (1000 * 60);
        console.log('🔍 Checking reminders:', {
            elapsedMinutes: elapsedMinutes.toFixed(2),
            schedule: this.reminderSchedule
        });
        
        for (let i = 0; i < this.reminderSchedule.length; i++) {
            const reminder = this.reminderSchedule[i];
            
            if (!reminder.triggered && elapsedMinutes >= reminder.timeMinutes) {
                // Check if enough time has passed since last reminder
                const lastReminderTime = this.reminderHistory.get('unified_reminder');
                if (lastReminderTime) {
                    const timeSinceLastReminder = (Date.now() - lastReminderTime) / (1000 * 60);
                    if (timeSinceLastReminder < this.config.unified.minIntervalMinutes) {
                        continue; // Skip this reminder, too soon
                    }
                }

                // Trigger the reminder
                reminder.triggered = true;
                this.triggerUnifiedReminder('scheduled', this.getContextualReminderMessage(elapsedMinutes));
                break; // Only trigger one reminder at a time
            }
        }
    }

    /**
     * Get contextual reminder message based on session progress
     */
    getContextualReminderMessage(elapsedMinutes) {
        const messages = [
            "How's the prep work going?",
            "How is this step going?",
            "How is the cooking starting off?",
            "How's everything progressing?",
            "How is the cooking going?",
            "How's the dish coming along?",
            "How did the cooking go overall?"
        ];

        // Select message based on progress through session
        const progress = Math.min(elapsedMinutes / 30, 1); // Assume 30min average session
        const messageIndex = Math.floor(progress * (messages.length - 1));
        
        return messages[messageIndex] || messages[messages.length - 1];
    }

    /**
     * Trigger a unified reminder (replaces separate photo/check-in reminders)
     */
    triggerUnifiedReminder(trigger, message) {
        // console.log(`🔔 Attempting unified reminder - enabled: ${this.isEnabled}, hasCallback: ${!!this.reminderCallbacks.onPhotoReminder}`);
        
        if (!this.isEnabled || !this.reminderCallbacks.onPhotoReminder) return;
        
        // Check session limit
        if (this.sessionCounters.reminders >= this.config.unified.maxPerSession) {
            // console.log('🔔 Reminder limit reached for session');
            return;
        }
        
        // Check timing interval
        if (this.shouldSkipReminder('unified_reminder', this.config.unified.minIntervalMinutes)) {
            // console.log('🔔 Skipping reminder - too soon since last reminder');
            return;
        }
        
        // console.log('🔔 Chef Unified Reminder:', message);
        
        const reminder = {
            type: 'unified',
            trigger,
            message,
            timestamp: new Date().toISOString(),
            stepIndex: this.sessionContext?.currentStep || 0,
            sessionId: this.sessionContext?.sessionId
        };
        
        // Increment counter and show reminder
        this.sessionCounters.reminders++;
        this.reminderCallbacks.onPhotoReminder(reminder); // Use photo callback for unified modal
        this.recordReminder('unified_reminder');
    }

    /**
     * Trigger a photo reminder
     */
    triggerPhotoReminder(trigger, message) {
        // console.log(`📸 Attempting photo reminder - enabled: ${this.isEnabled}, hasCallback: ${!!this.reminderCallbacks.onPhotoReminder}`);
        
        if (!this.isEnabled || !this.reminderCallbacks.onPhotoReminder) return;
        
        // Check session limit
        // console.log(`📸 Photo counter: ${this.sessionCounters.photos}/${this.config.unified.maxPerSession}`);
        if (this.sessionCounters.photos >= this.config.unified.maxPerSession) {
            // console.log('📸 Photo reminder limit reached for session');
            return;
        }
        
        // Check if current phase should trigger photo
        const currentPhase = this.getCurrentPhase();
        // console.log(`📸 Current phase: '${currentPhase}', trigger phases:`, ['prep', 'cook', 'finish']);
        if (!['prep', 'cook', 'finish'].includes(currentPhase)) {
            // console.log(`📸 Skipping photo reminder - phase '${currentPhase}' not in trigger list`);
            return;
        }
        
        // Check timing interval
        const reminderKey = `photo_${currentPhase}`;
        if (this.shouldSkipReminder(reminderKey, this.config.unified.minIntervalMinutes)) {
            // console.log(`📸 Skipping photo reminder - too soon since last reminder`);
            return;
        }
        
        // console.log('📸 Chef Photo Reminder:', message);
        
        const reminder = {
            type: 'photo',
            trigger,
            message,
            timestamp: new Date().toISOString(),
            stepIndex: this.sessionContext?.currentStep || 0,
            sessionId: this.sessionContext?.sessionId
        };
        
        // Increment counter and show reminder
        this.sessionCounters.photos++;
        this.lastReminderType = 'photo'; // Track for alternation
        this.reminderCallbacks.onPhotoReminder(reminder);
        this.recordReminder(reminderKey);
    }

    /**
     * Trigger a check-in reminder
     */
    triggerCheckInReminder(trigger, message) {
        // console.log(`💬 Attempting check-in reminder - enabled: ${this.isEnabled}, hasCallback: ${!!this.reminderCallbacks.onCheckInReminder}`);
        
        if (!this.isEnabled || !this.reminderCallbacks.onCheckInReminder) return;
        
        // Check session limit
        // console.log(`💬 Check-in counter: ${this.sessionCounters.checkIns}/${this.config.unified.maxPerSession}`);
        if (this.sessionCounters.checkIns >= this.config.unified.maxPerSession) {
            // console.log('💬 Check-in reminder limit reached for session');
            return;
        }
        
        // Check if current phase should trigger check-in
        const currentPhase = this.getCurrentPhase();
        // console.log(`💬 Current phase: '${currentPhase}', trigger phases:`, ['prep', 'cook', 'finish']);
        if (!['prep', 'cook', 'finish'].includes(currentPhase)) {
            // console.log(`💬 Skipping check-in reminder - phase '${currentPhase}' not in trigger list`);
            return;
        }
        
        // Check timing interval
        const reminderKey = `checkin_${currentPhase}`;
        if (this.shouldSkipReminder(reminderKey, this.config.unified.minIntervalMinutes)) {
            // console.log(`💬 Skipping check-in reminder - too soon since last reminder`);
            return;
        }
        
        // console.log('💬 Chef Check-in Reminder:', message);
        
        const reminder = {
            type: 'checkin',
            trigger,
            message,
            timestamp: new Date().toISOString(),
            stepIndex: this.sessionContext?.currentStep || 0,
            sessionId: this.sessionContext?.sessionId
        };
        
        // Increment counter and show reminder
        this.sessionCounters.checkIns++;
        this.lastReminderType = 'checkin'; // Track for alternation
        this.reminderCallbacks.onCheckInReminder(reminder);
        this.recordReminder(reminderKey);
    }

    /**
     * Check if we should skip a reminder based on timing
     */
    shouldSkipReminder(reminderKey, minIntervalMinutes) {
        const lastShown = this.reminderHistory.get(reminderKey);
        if (!lastShown) return false;
        
        const timeSinceLastShown = Date.now() - lastShown;
        const minIntervalMs = minIntervalMinutes * 60 * 1000;
        
        return timeSinceLastShown < minIntervalMs;
    }

    /**
     * Record that a reminder was shown
     */
    recordReminder(reminderKey) {
        this.reminderHistory.set(reminderKey, Date.now());
    }

    /**
     * Add timeline entry for a photo reminder
     */
    async addPhotoTimelineEntry(photoData, reminder) {
        if (!this.sessionContext?.sessionId || !this.userToken) {
            console.warn('Cannot add photo timeline entry: missing session ID or token');
            return;
        }

        try {
            // Convert imageData (base64) to url format for display
            const photo = {
                ...photoData,
                url: photoData.imageData || photoData.url, // Use imageData as data URL
                caption: reminder.message
            };
            
            // Remove redundant fields
            delete photo.blob;
            delete photo.imageData; // Keep original in case needed, but use url for display
            
            const timelineEntry = {
                timestamp: new Date().toISOString(),
                phase: this.getCurrentPhase(),
                notes: `Photo captured: ${reminder.message}`,
                photos: [photo],
                status: 'ok',
                source: 'chef_reminder'
            };

            await sessionReportService.addTimelineEntry(
                this.sessionContext.sessionId,
                timelineEntry,
                this.userToken
            );

            // console.log('📸 Added photo timeline entry:', timelineEntry);
        } catch (error) {
            console.error('Error adding photo timeline entry:', error);
        }
    }

    /**
     * Process voice input through AI for better formatting
     */
    async processVoiceInput(text) {
        try {
            const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001/api';
            
            const response = await fetch(`${API_BASE_URL}/cook/process-checkin`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.userToken}`
                },
                body: JSON.stringify({ text })
            });
            
            if (!response.ok) {
                console.warn('AI processing failed, using original text');
                return text;
            }
            
            const data = await response.json();
            return data.processedText || text;
        } catch (error) {
            console.error('Error processing voice input with AI:', error);
            return text;
        }
    }

    /**
     * Detect if user wants to stop providing information
     */
    async detectStopIntent(text) {
        try {
            const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001/api';
            
            const response = await fetch(`${API_BASE_URL}/cook/detect-stop-intent`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.userToken}`
                },
                body: JSON.stringify({ text })
            });
            
            if (!response.ok) {
                console.warn('Stop intent detection failed, assuming continue');
                return { shouldStop: false, confidence: 0 };
            }
            
            const data = await response.json();
            return { shouldStop: data.shouldStop || false, confidence: data.confidence || 0 };
        } catch (error) {
            console.error('Error detecting stop intent:', error);
            return { shouldStop: false, confidence: 0 };
        }
    }

    /**
     * Process check-in response through AI for better formatting
     */
    async processCheckInWithAI(rawResponse) {
        try {
            const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001/api';
            
            const response = await fetch(`${API_BASE_URL}/cook/process-checkin`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${this.userToken}`
                },
                body: JSON.stringify({ text: rawResponse })
            });
            
            if (!response.ok) {
                // If AI processing fails, return original
                console.warn('AI processing failed, using original text');
                return rawResponse;
            }
            
            const data = await response.json();
            return data.processedText || rawResponse;
        } catch (error) {
            console.error('Error processing check-in with AI:', error);
            // Fallback to original text
            return rawResponse;
        }
    }

    /**
     * Add timeline entry for a check-in reminder
     */
    async addCheckInTimelineEntry(response, reminder) {
        if (!this.sessionContext?.sessionId || !this.userToken) {
            console.warn('Cannot add check-in timeline entry: missing session ID or token');
            return;
        }

        try {
            // Process response through AI for better formatting
            // console.log('💬 Raw check-in response:', response);
            const processedResponse = await this.processCheckInWithAI(response);
            // console.log('💬 AI-processed check-in:', processedResponse);
            
            const timelineEntry = {
                timestamp: new Date().toISOString(),
                phase: this.getCurrentPhase(),
                notes: `Check-in: ${processedResponse}`,
                photos: [],
                status: this.determineStatusFromResponse(processedResponse),
                source: 'chef_reminder'
            };

            await sessionReportService.addTimelineEntry(
                this.sessionContext.sessionId,
                timelineEntry,
                this.userToken
            );

            // console.log('💬 Added check-in timeline entry:', timelineEntry);
        } catch (error) {
            console.error('Error adding check-in timeline entry:', error);
        }
    }

    /**
     * Get current cooking phase based on session context
     */
    getCurrentPhase() {
        if (!this.sessionContext) {
            // console.log('⚠️ No session context, defaulting to cooking phase');
            return 'cooking';
        }
        
        // Map step index to phase with more granular detection
        const stepIndex = this.sessionContext.currentStep || 0;
        const totalSteps = this.sessionContext.totalSteps || 1;
        
        // console.log('🔔 Phase calculation:', { stepIndex, totalSteps });
        
        // More sophisticated phase detection
        if (stepIndex === 0) return 'prep';
        if (stepIndex === totalSteps - 1) return 'finishing';
        if (stepIndex < totalSteps * 0.3) return 'early_cooking';
        if (stepIndex < totalSteps * 0.7) return 'cooking';
        return 'late_cooking';
    }

    /**
     * Determine status from check-in response
     */
    determineStatusFromResponse(response) {
        const lowerResponse = response.toLowerCase();
        
        if (lowerResponse.includes('problem') || lowerResponse.includes('issue') || 
            lowerResponse.includes('difficult') || lowerResponse.includes('struggling')) {
            return 'needs_attention';
        }
        
        if (lowerResponse.includes('great') || lowerResponse.includes('perfect') || 
            lowerResponse.includes('excellent') || lowerResponse.includes('amazing')) {
            return 'excellent';
        }
        
        return 'ok';
    }

    /**
     * Get phase-aware photo reminder message
     */
    getPhaseAwarePhotoMessage(phase) {
        const messages = {
            'prep': 'Great prep work! Want to capture your mise en place?',
            'early_cooking': 'Perfect timing! Want to snap a photo of the cooking process?',
            'cooking': 'Looking good! Want to capture this cooking moment?',
            'late_cooking': 'Almost there! Want to document the final cooking steps?',
            'finishing': 'Beautiful! Want to capture the final presentation?'
        };
        return messages[phase] || 'Great progress! Want to snap a photo of this step?';
    }

    /**
     * Get phase-aware check-in message
     */
    getPhaseAwareCheckInMessage(phase) {
        const messages = {
            'prep': 'How is the prep work going?',
            'early_cooking': 'How is the cooking starting off?',
            'cooking': 'How is this step going?',
            'late_cooking': 'How are the final cooking steps going?',
            'finishing': 'How is the finishing going?'
        };
        return messages[phase] || 'How is this step going?';
    }

    /**
     * Get step start check-in message
     */
    getStepStartCheckInMessage(phase) {
        const messages = {
            'prep': 'Ready to start prepping? How are you feeling about this step?',
            'early_cooking': 'Starting the cooking process! How does everything look so far?',
            'cooking': 'New step starting! How is the cooking going?',
            'late_cooking': 'Moving into the final cooking steps! How is everything progressing?',
            'finishing': 'Almost done! How is the finishing going?'
        };
        return messages[phase] || 'Starting a new step! How is everything going?';
    }

    /**
     * Clear reminder history (useful when starting new session)
     */
    clearReminderHistory() {
        this.reminderHistory.clear();
        // Reset session counters
        this.sessionCounters = {
            photos: 0,
            checkIns: 0
        };
    }

    /**
     * Get reminder statistics
     */
    getReminderStats() {
        return {
            isEnabled: this.isEnabled,
            totalReminders: this.reminderHistory.size,
            reminderTypes: {
                photo: Array.from(this.reminderHistory.keys()).filter(k => k.startsWith('photo_')).length,
                checkin: Array.from(this.reminderHistory.keys()).filter(k => k.startsWith('checkin_')).length
            },
            sessionCounters: {
                photos: this.sessionCounters.photos,
                checkIns: this.sessionCounters.checkIns,
                photoLimit: this.config.unified.maxPerSession,
                checkInLimit: this.config.unified.maxPerSession
            }
        };
    }

    /**
     * Update reminder configuration
     */
    updateConfig(newConfig) {
        this.config = { ...this.config, ...newConfig };
    }

    /**
     * Process voice input with AI to make it coherent
     * @param {string} transcript - Raw voice transcript
     * @returns {Promise<string>} Formatted text
     */
    async processVoiceInput(transcript) {
        if (!transcript || !transcript.trim()) {
            return '';
        }

        if (!this.sessionContext?.sessionId || !this.userToken) {
            console.warn('Cannot process voice: missing session ID or token');
            return transcript; // Return original if can't process
        }

        try {
            const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
            console.log('🔍 Voice processing API call:', {
                url: `${API_BASE_URL}/api/sessions/${this.sessionContext.sessionId}/process-voice`,
                sessionId: this.sessionContext.sessionId,
                hasToken: !!this.userToken,
                transcript: transcript.substring(0, 50) + '...'
            });
            
            const response = await fetch(
                `${API_BASE_URL}/api/sessions/${this.sessionContext.sessionId}/process-voice`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${this.userToken}`
                    },
                    body: JSON.stringify({
                        transcript,
                        context: {
                            stepIndex: this.sessionContext.currentStep,
                            stepText: this.sessionContext.currentStepText || '',
                            phase: this.sessionContext.phase || 'active'
                        }
                    })
                }
            );

            if (!response.ok) {
                const errorText = await response.text();
                console.error('Voice processing API error:', {
                    status: response.status,
                    statusText: response.statusText,
                    error: errorText,
                    sessionId: this.sessionContext.sessionId
                });
                throw new Error(`Failed to process voice input: ${response.status} ${response.statusText}`);
            }

            const result = await response.json();
            const processedText = result.formattedText || transcript;
            
            // Track voice processing interaction
            this.trackVoiceProcessingInteraction(transcript, processedText);
            
            return processedText;

        } catch (error) {
            console.error('Error processing voice input:', error);
            return transcript; // Return original on error
        }
    }

    /**
     * Detect stop intent from user response using AI
     * @param {string} text - User's text response
     * @returns {Promise<Object>} Stop intent detection result
     */
    async detectStopIntent(text) {
        if (!text || !text.trim()) {
            return {
                shouldStop: false,
                confidence: 0
            };
        }

        if (!this.sessionContext?.sessionId || !this.userToken) {
            console.warn('Cannot detect stop intent: missing session ID or token');
            // Fallback to simple pattern matching
            return this.simpleStopDetection(text);
        }

        try {
            const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
            const response = await fetch(
                `${API_BASE_URL}/api/sessions/${this.sessionContext.sessionId}/process-voice`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${this.userToken}`
                    },
                    body: JSON.stringify({
                        transcript: text,
                        context: {
                            stepIndex: this.sessionContext.currentStep,
                            stepText: this.sessionContext.currentStepText || '',
                            phase: this.sessionContext.phase || 'active'
                        }
                    })
                }
            );

            if (!response.ok) {
                throw new Error('Failed to detect stop intent');
            }

            const result = await response.json();
            return result.stopIntent || { shouldStop: false, confidence: 0 };

        } catch (error) {
            console.error('Error detecting stop intent:', error);
            // Fallback to simple detection
            return this.simpleStopDetection(text);
        }
    }

    /**
     * Simple stop intent detection (fallback)
     * @param {string} text - User's text
     * @returns {Object} Stop intent result
     */
    simpleStopDetection(text) {
        const lowerText = text.toLowerCase().trim();
        const stopPhrases = [
            'no', 'nope', 'nothing', 'nothing else', 'nothing more',
            "that's it", "that's all", "done", "i'm done", "im done"
        ];

        const hasStopPhrase = stopPhrases.some(phrase => 
            lowerText === phrase || 
            lowerText.startsWith(phrase + ' ') || 
            lowerText.endsWith(' ' + phrase)
        );

        return {
            shouldStop: hasStopPhrase,
            confidence: hasStopPhrase ? 0.8 : 0.2
        };
    }

    /**
     * Track voice processing interaction in session report
     * @param {string} originalTranscript - Original voice transcript
     * @param {string} processedTranscript - AI-processed transcript
     */
    trackVoiceProcessingInteraction(originalTranscript, processedTranscript) {
        // Import sessionReport dynamically to avoid circular dependency
        import('./sessionReport').then(({ default: sessionReport }) => {
            sessionReport.addVoiceProcessingInteraction(originalTranscript, processedTranscript, 
                this.sessionContext?.currentStep || -1, {
                phase: 'reminder',
                stepText: this.sessionContext?.currentStepText || ''
            });
        }).catch(error => {
            console.warn('Could not track voice processing interaction:', error);
        });
    }

    /**
     * Cleanup event listeners
     */
    cleanup() {
        if (this.boundHandlers) {
            // console.log('🔔 ChefReminders: Cleaning up event listeners');
            liveSessionEvents.unsubscribe('session.start', this.boundHandlers.sessionStart);
            liveSessionEvents.unsubscribe('phase.change', this.boundHandlers.phaseChange);
            liveSessionEvents.unsubscribe('timer.tick', this.boundHandlers.timerTick);
            liveSessionEvents.unsubscribe('session.end', this.boundHandlers.sessionEnd);
        }
    }
}

// Create singleton instance
// // console.log('🔔 ChefReminders: Creating singleton instance');
const chefReminders = new ChefRemindersService();
// // console.log('🔔 ChefReminders: Singleton created successfully');

export default chefReminders;
